import { formatDistanceToNow } from 'date-fns';
import * as React from 'react';

import { cn } from '@/lib/utils';
import type {
  AgentType,
  CustomUserSchema,
} from '@/services/api/generated/taskActivityService';

interface EventContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const EventContainer: React.FC<EventContainerProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'border-b border-slate-100 py-1.5 px-2 hover:bg-slate-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50 transition-colors',
        className,
      )}
    >
      {children}
    </div>
  );
};

interface EventHeaderProps {
  icon: React.ReactNode;
  title: string;
  timestamp: string | undefined;
  actor: CustomUserSchema | null | undefined;
  agent: AgentType | null | undefined;
}

export const EventHeader: React.FC<EventHeaderProps> = ({
  icon,
  title,
  timestamp,
  actor,
  agent,
}) => {
  const actorName = actor
    ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() || actor.email
    : agent
      ? formatAgentName(agent)
      : 'System';

  const formattedTime = timestamp
    ? formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    : '';

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left side: Icon + Title */}
      <div className="flex items-center gap-1.5 min-w-0 flex-shrink">
        <div className="text-slate-500 dark:text-zinc-400 shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5">{icon}</div>
        <span className="font-semibold text-xs text-slate-900 dark:text-zinc-100 truncate">
          {title}
        </span>
      </div>

      {/* Right side: Actor + Date */}
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-zinc-400 shrink-0">
        <span className="font-medium truncate">{actorName}</span>
        <span className="text-slate-400 dark:text-zinc-500">·</span>
        <span className="font-medium text-slate-500 dark:text-zinc-400 whitespace-nowrap">
          {formattedTime}
        </span>
      </div>
    </div>
  );
};

interface EventBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const EventBody: React.FC<EventBodyProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'text-xs font-medium text-slate-600 dark:text-zinc-300 ml-[20px] mt-0.5',
        className,
      )}
    >
      {children}
    </div>
  );
};

interface EventActionsProps {
  children: React.ReactNode;
  className?: string;
}

export const EventActions: React.FC<EventActionsProps> = ({
  children,
  className,
}) => {
  return (
    <div className={cn('flex items-center gap-2 mt-1 ml-[20px]', className)}>
      {children}
    </div>
  );
};

// Helper function to format agent names
function formatAgentName(agent: AgentType): string {
  const agentNames: Record<string, string> = {
    ingest_transcript_task: 'Transcript Ingestion',
    provider_finder: 'Provider Finder',
    artifact_digest: 'Artifact Digest',
    treatment_researcher: 'Treatment Researcher',
    treatment_request_automation: 'Treatment Request Automation',
    bland_caller: 'Voice AI',
    slack_task_bot: 'Task Bot',
    blob_sorter: 'Document Sorter',
    front_app_integration: 'Front Integration',
    task_generator: 'Task Generator',
    coverage_extractor: 'Coverage Extractor',
    intake_converter: 'Intake Converter',
    airtable_sync: 'Airtable Sync',
    system: 'System',
    backfill_script: 'Backfill Script',
    domain_event_update_orchestrator: 'Event Orchestrator',
    loa_detector: 'LOA Detector',
    rainmaker: 'Rainmaker',
    deliverable_sync: 'Deliverable Sync',
    bpo_agent: 'BPO',
    loa_chaser: 'LOA Chaser',
    external_task_rule_engine: 'External Task Rules',
    external_task_bulk_create: 'External Task Bulk Create',
    entity_sync: 'Entity Sync',
    case_activation: 'Case Activation',
    upwork: 'Upwork',
    crashing_out: 'Crashing Out',
  };

  return agentNames[agent] || agent;
}
